import { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { GraphQLError } from 'graphql';

import { GraphQLErrorWithDebugMessage } from './types';

/**
 * Check if error is a FetchBaseQueryError (e.g. network/server/API error)
 */
export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

/**
 * Check if error is a SerializedError (e.g. JS thrown error)
 */
export function isSerializedError(error: unknown): error is SerializedError {
  return typeof error === 'object' && error !== null && 'message' in error;
}

/**
 * Check if error is an instance of GraphQLError
 */
export function isGraphQLError(error: unknown): error is GraphQLError {
  return typeof error === 'object' && error !== null && 'message' in error && 'extensions' in error;
}

export function isGraphQLErrorArray(error: unknown): error is GraphQLError[] {
  return Array.isArray(error) && error.length > 0 && error.every(e => isGraphQLError(e));
}

export function isGraphQLErrorWithDebugMessage(error: GraphQLError): error is GraphQLErrorWithDebugMessage {
  const { extensions } = error;
  return extensions && typeof extensions.debugMessage === 'string';
}

export function isGraphQLErrorWithDebugObject(error: GraphQLError): error is GraphQLErrorWithDebugMessage {
  const { extensions } = error;
  if (extensions?.debugMessage) {
    const { debugMessage } = extensions;
    return typeof debugMessage === 'object' && debugMessage !== null && 'message' in debugMessage;
  }
  return false;
}
